/**
 * @file delivery-quote.ts
 * @description Модель формы расчёта доставки (phase19.12 шаг 5).
 *
 * Чистые функции без React: черновики мест, преобразование формы в тело
 * запроса `POST /admin/shop/carriers/:carrier/quote` и в места отгрузки,
 * валидация перед расчётом, форматтеры.
 *
 * Решения пользователя: мест (коробок) может быть несколько; дробление одной
 * позиции между местами запрещено — позиция лежит ровно в одном месте.
 */

import type { DeliveryQuoteSnapshot } from "./api-delivery-agreement";
import type {
  OrderDeliveryDto,
  ShipmentCarrier,
  ShipmentDto,
  ShipmentPackageInput,
} from "./api-orders";
import type { AdminDeliveryQuoteInput, DeliveryQuoteOption } from "./api-shop-carriers";
import { isToDoorTariff } from "./delivery-quote-meta";

/** Куда везём — совпадает с режимом позиции доставки в заказе. */
export type DeliveryDestinationMode = "PVZ" | "COURIER";

/**
 * Часть ответа о доставке, которой хватает калькулятору: куда везём и что
 * везём. Узкий тип, а не весь `OrderDeliveryDto`, — чтобы расчёт не зависел от
 * полей согласования (право на правку, заявка, перевозчики с интеграцией) и не
 * заставлял каждого вызывающего собирать их ради формы мест.
 */
export type DeliveryQuoteSource = Pick<
  OrderDeliveryDto,
  "deliveryAddress" | "deliveryItem" | "items"
>;

/** Черновик места: значения полей — строки, парсим только при отправке. */
export interface DeliveryPackageDraft {
  /** Локальный ключ для React (id места из API тут не нужен). */
  key: string;
  weightG: string;
  lengthMm: string;
  widthMm: string;
  heightMm: string;
  comment: string;
  /** Позиции заказа, уложенные в это место. */
  orderItemIds: string[];
  /**
   * Поля места правили руками: автоподстановка по товарам больше не трогает
   * их сама, вернуть расчётные значения можно кнопкой «Рассчитать по товарам».
   */
  touched: boolean;
}

/** Состояние формы расчёта — общий контракт value/onChange для секций. */
export interface DeliveryQuoteFormValue {
  mode: DeliveryDestinationMode;
  pickupPointCode: string;
  city: string;
  cityCode: number | null;
  postalCode: string;
  address: string;
  packages: DeliveryPackageDraft[];
}

let packageSeq = 0;

/** Новое пустое место (вес считает сервер по позициям, если поле пустое). */
export function createPackageDraft(
  patch: Partial<DeliveryPackageDraft> = {},
): DeliveryPackageDraft {
  packageSeq += 1;
  return {
    key: `pkg-${packageSeq}`,
    weightG: "",
    lengthMm: "",
    widthMm: "",
    heightMm: "",
    comment: "",
    orderItemIds: [],
    touched: false,
    ...patch,
  };
}

/** Пустая форма — для справочного калькулятора в настройках перевозчика. */
export function emptyQuoteForm(): DeliveryQuoteFormValue {
  return {
    mode: "PVZ",
    pickupPointCode: "",
    city: "",
    cityCode: null,
    postalCode: "",
    address: "",
    packages: [createPackageDraft()],
  };
}

/**
 * Предзаполнение из заказа: адрес и режим — из выбора клиента на витрине,
 * все позиции складываются в первое место (менеджер потом раскладывает).
 */
export function formFromOrderDelivery(
  delivery: DeliveryQuoteSource | null,
): DeliveryQuoteFormValue {
  const address = delivery?.deliveryAddress ?? null;
  const mode: DeliveryDestinationMode =
    delivery?.deliveryItem?.mode ??
    (address?.mode === "COURIER" ? "COURIER" : "PVZ");
  return {
    mode,
    pickupPointCode: address?.pickupPointCode ?? "",
    city: address?.city ?? "",
    cityCode: address?.cityCode ?? null,
    postalCode: address?.postalCode ?? "",
    address: address?.address ?? "",
    packages: [
      createPackageDraft({
        orderItemIds: (delivery?.items ?? []).map((i) => i.orderItemId),
      }),
    ],
  };
}

/**
 * Предзаполнение из отгрузки: места и ПВЗ уже сохранены — расчёт открывается
 * ровно с теми параметрами, с которыми поедет заявка.
 */
export function formFromShipment(
  shipment: ShipmentDto,
  base: DeliveryQuoteFormValue,
): DeliveryQuoteFormValue {
  const packages = shipment.packages ?? [];
  return {
    ...base,
    mode: shipment.tariffCode !== null ? modeByTariffCode(shipment.tariffCode) : base.mode,
    pickupPointCode: shipment.pickupPointCode ?? base.pickupPointCode,
    packages:
      packages.length > 0
        ? packages.map((p, idx) =>
            createPackageDraft({
              weightG: String(p.weightG),
              lengthMm: p.lengthMm === null ? "" : String(p.lengthMm),
              widthMm: p.widthMm === null ? "" : String(p.widthMm),
              heightMm: p.heightMm === null ? "" : String(p.heightMm),
              comment: p.comment ?? "",
              touched: true,
              // Раскладка позиций по местам с сервера не приходит: всё,
              // что известно, кладём в первое место.
              orderItemIds: idx === 0 ? base.packages[0]?.orderItemIds ?? [] : [],
            }),
          )
        : base.packages,
  };
}

/** Пустая строка → null; «1 200» и «1,2» тоже разбираем. */
export function toNumber(value: string): number | null {
  const trimmed = value.trim().replace(/\s/gu, "").replace(",", ".");
  if (!trimmed) return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
}

/** Суммарный вес мест (0 у мест с пустым весом — их считает сервер). */
export function totalWeightG(packages: DeliveryPackageDraft[]): number {
  return packages.reduce((sum, p) => sum + (toNumber(p.weightG) ?? 0), 0);
}

/**
 * Проблема, из-за которой расчёт запускать нельзя. Возвращаем поле для фокуса,
 * чтобы менеджер сразу попал в нужный инпут.
 */
export interface QuoteFormIssue {
  field: "city" | "weight";
  packageIndex?: number;
  message: string;
}

/**
 * Вес обязателен, когда мест несколько: сервер умеет считать вес по позициям
 * только для одного места (иначе непонятно, что в какой коробке).
 */
export function validateQuoteForm(
  value: DeliveryQuoteFormValue,
  hasItems: boolean,
): QuoteFormIssue | null {
  if (!value.pickupPointCode.trim() && !value.city.trim() && value.cityCode === null) {
    return { field: "city", message: "Укажите город получателя или выберите ПВЗ." };
  }
  const single = value.packages.length === 1;
  for (let i = 0; i < value.packages.length; i += 1) {
    const weight = toNumber(value.packages[i]?.weightG ?? "");
    if (weight !== null && weight > 0) continue;
    if (single && hasItems) continue; // вес посчитает сервер по позициям
    return {
      field: "weight",
      packageIndex: i,
      message: single
        ? "Укажите вес места — по позициям заказа вес рассчитать не удалось."
        : `Укажите вес места ${i + 1}: при нескольких местах вес задаётся вручную.`,
    };
  }
  return null;
}

/** Черновики мест → тело create/patch отгрузки. */
export function packagesToInput(
  packages: DeliveryPackageDraft[],
  fallbackWeightG = 0,
): ShipmentPackageInput[] {
  return packages.map((p, idx) => ({
    number: idx + 1,
    weightG: toNumber(p.weightG) ?? fallbackWeightG,
    lengthMm: toNumber(p.lengthMm),
    widthMm: toNumber(p.widthMm),
    heightMm: toNumber(p.heightMm),
    comment: p.comment.trim() || null,
    orderItemIds: p.orderItemIds,
  }));
}

/** «120×80×40 мм»; неполные габариты → null. */
export function formatDims(
  length: number | null,
  width: number | null,
  height: number | null,
): string | null {
  if (length === null || width === null || height === null) return null;
  return `${length}×${width}×${height} мм`;
}

/** Позиции заказа для запроса расчёта (сервер сам сложит их вес). */
export interface QuoteItemsSource {
  items?: Array<{ skuId: string; quantity: number }>;
  goodsTotal?: number;
}

/**
 * Форма → тело запроса расчёта. Если место одно и вес не задан руками —
 * отправляем позиции, чтобы вес посчитал сервер (единый источник правды).
 */
export function buildQuoteInput(
  value: DeliveryQuoteFormValue,
  source: QuoteItemsSource = {},
): AdminDeliveryQuoteInput {
  const input: AdminDeliveryQuoteInput = {};
  if (value.mode === "PVZ" && value.pickupPointCode.trim()) {
    input.pickupPointCode = value.pickupPointCode.trim();
  }
  if (value.cityCode !== null) input.cityCode = value.cityCode;
  if (value.city.trim()) input.city = value.city.trim();
  if (value.postalCode.trim()) input.postalCode = value.postalCode.trim();
  if (value.address.trim()) input.address = value.address.trim();
  if (source.goodsTotal !== undefined) input.goodsTotal = source.goodsTotal;

  const explicit = value.packages.filter((p) => (toNumber(p.weightG) ?? 0) > 0);
  const useServerWeight =
    value.packages.length === 1 && explicit.length === 0 && !!source.items?.length;

  if (useServerWeight) {
    // Место с нулевым весом сервер считает пустым, поэтому в этом режиме
    // отправляем только позиции: вес и коробку подставит бэкенд по настройкам.
    input.items = source.items;
    return input;
  }

  input.packages = value.packages.map((p) => ({
    weightG: toNumber(p.weightG) ?? 0,
    lengthMm: toNumber(p.lengthMm) ?? undefined,
    widthMm: toNumber(p.widthMm) ?? undefined,
    heightMm: toNumber(p.heightMm) ?? undefined,
  }));
  return input;
}

/**
 * Снапшот выбранного тарифа для исхода согласования (phase19.24 шаг 5.2).
 *
 * Собираем здесь, а не в компоненте: тем же снапшотом сервер заполняет
 * позицию-услугу «Доставка» и будущую отгрузку, и расхождение «в форме одно,
 * в заказе другое» ловится ровно на этой функции.
 *
 * `null` — снапшот приложить нельзя: сервер не принимает расчёт без города
 * (`DeliveryQuoteDestinationDto.city`), а выдумывать город из кода ПВЗ хуже,
 * чем сохранить сумму без тарифа. Вызывающая форма обязана сказать об этом
 * менеджеру, а не проглотить молча.
 *
 * Вес мест и цену перевозчика отдаём так, как их называет сервер:
 * `weightGrams` и рубли строкой — иначе `forbidNonWhitelisted` и `Decimal`
 * встретят нас 400-м на валидном расчёте.
 */
export function buildQuoteSnapshot(
  option: DeliveryQuoteOption,
  value: DeliveryQuoteFormValue,
  carrier: ShipmentCarrier,
  /** Вес, посчитанный сервером по позициям — им закрываем пустые места. */
  fallbackWeightG = 0,
): DeliveryQuoteSnapshot | null {
  const city = value.city.trim();
  if (!city) return null;
  const pickupPointCode = value.pickupPointCode.trim();
  const postalCode = value.postalCode.trim();
  const address = value.address.trim();
  const packages = value.packages
    .map((p) => ({
      weightGrams: toNumber(p.weightG) ?? fallbackWeightG,
      lengthMm: toNumber(p.lengthMm),
      widthMm: toNumber(p.widthMm),
      heightMm: toNumber(p.heightMm),
    }))
    // Место без веса сервер отвергает (`Min(1)`): такие места не отправляем —
    // габариты без веса тариф всё равно не определяют.
    .filter((p) => p.weightGrams > 0);
  return {
    carrier,
    tariffCode: option.tariffCode,
    tariffName: option.tariffName,
    // Режим — словами сервера: числовой `delivery_mode` он тоже принимает, но
    // тогда смысл тарифа читался бы только через справочник СДЭК.
    deliveryMode: isToDoorTariff(option) ? "DOOR" : "PVZ",
    carrierPrice: option.carrierPrice.toFixed(2),
    periodMin: option.periodMin,
    periodMax: option.periodMax,
    quotedAt: new Date().toISOString(),
    destination: {
      city,
      ...(value.cityCode !== null ? { cityCode: value.cityCode } : {}),
      ...(postalCode ? { postalCode } : {}),
      ...(value.mode === "PVZ" && pickupPointCode ? { pickupPointCode } : {}),
      ...(address ? { address } : {}),
    },
    ...(packages.length > 0 ? { packages } : {}),
  };
}

/** Тариф → режим доставки для предзаполнения формы (см. SDEK_TARIFF_META). */
function modeByTariffCode(code: number): DeliveryDestinationMode {
  return code === 137 || code === 139 ? "COURIER" : "PVZ";
}

/** «1 250 ₽»; null → «—». */
export function formatRub(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  return `${value.toLocaleString("ru-RU", { maximumFractionDigits: 2 })} ₽`;
}

/** «1 200 г» / «1,2 кг» — граммы читаются плохо на больших весах. */
export function formatWeight(grams: number | null | undefined): string {
  if (grams === null || grams === undefined || !Number.isFinite(grams)) return "—";
  if (grams < 1000) return `${grams} г`;
  return `${(grams / 1000).toLocaleString("ru-RU", { maximumFractionDigits: 2 })} кг`;
}

/** «1–3 дня» из периода тарифа. */
export function formatPeriod(min: number | null, max: number | null): string {
  if (min === null && max === null) return "срок не указан";
  if (min !== null && max !== null && min !== max) return `${min}–${max} дн.`;
  return `${max ?? min} дн.`;
}
